// =============================================================================
// domain/share.ts — what a public share link is allowed to contain.
// -----------------------------------------------------------------------------
// Relocated from the old client's `_shareTradeData`, `SHARE_EXPIRY_CHOICES` and
// the visitor table's `disc()` (app/index.html:29170-29290, :29840). Not
// redesigned — this feature was hardened over five commits ending `e8441e1`
// after a real link went out with too much in it, and the reasoning below is
// that hardening rather than a fresh opinion.
//
// ── 🚨 THE ALLOW-LIST IS THE PRIVACY CONTROL. NOT THE RENDERER ──
//
// Whatever this function returns is readable in the visitor's Network tab
// whether or not anything draws it. So "the modal does not show X" is worth
// exactly nothing; the only control that means anything is X not being here.
// Four things are therefore absent BY CONSTRUCTION rather than hidden:
//
//   recordings    the sharer's voice notes. Thinking out loud mid-session is not
//                 review material, and there is no toggle for it — it never
//                 travels.
//   executions    the per-fill tape (bought 3 at 09:31:07, 4 more at 09:33:22).
//                 That is the sizing METHOD rather than the result, and across a
//                 run of shared days it is the reproducible part of an edge.
//   riskAtEntry   dollars risked. Points and R say the same thing to a reviewer
//                 without implying account size.
//   sourceAccount the raw broker string. Stripped from the captured HTML too
//                 (D213); it must not sneak back in through the data.
//
// ⚠ FREE TEXT IS GATED BY ABSENCE, not by rendering. With notes off the fields
// are not written at all, rather than written and then not drawn.
//
// ── 🚨 EVERY ID IS RESOLVED TO A LABEL HERE ──
//
// The visitor has no state: no patterns, no exit models, no tag vocabulary, no
// accounts. A pattern id in the payload renders as `pat_8f3a…` on a public page,
// which reads as data corruption rather than as a setup name. So nothing that
// needs a lookup may leave this module unresolved.
//
// ⚠ Pure, and deliberately so: the registries arrive as arguments rather than as
// imports. That is what lets the whole allow-list be tested without a browser,
// a database or a signed-in user — and the allow-list is the part where being
// wrong is a disclosure rather than a defect.
// =============================================================================

import type { PnlUnit } from './types';

// ---------------------------------------------------------------------------
// How long a link lives
// ---------------------------------------------------------------------------

export interface ShareExpiryChoice {
  label: string;
  /** 0 means never. */
  hours: number;
  /** Exactly one entry carries this. */
  def?: boolean;
}

/**
 * ── 🚨 FOUR HOURS IS THE DEFAULT, AND THE TWO MISTAKES ARE NOT EQUAL ──
 *
 * A link that dies too soon costs one click to recreate. A link that lives too
 * long is a public record of somebody's trading — entry and exit prices, size,
 * grades, discipline breaches — on a guessable-by-nobody but forwardable-by-
 * anyone URL, for as long as nobody remembers to revoke it.
 *
 * ⚠ DEFAULTS ARE WHAT ACTUALLY SHIP. Most links will be made by someone who
 * never opens this menu, so whatever sits here is the real policy of the
 * feature. It started at "never", then 30 days; four hours is Josh's call and
 * matches the actual use — a link is nearly always sent for a conversation
 * happening now.
 *
 * ⚠ NOT REMEMBERED BETWEEN SHARES, and that is the whole ordering. Persisting
 * the last choice would mean one deliberate "Never" quietly becomes the default
 * for every share after it.
 *
 * "Never" is still offered — a link in a published video should not die — but it
 * is a decision somebody makes rather than a state they land in.
 */
export const SHARE_EXPIRY_CHOICES: readonly ShareExpiryChoice[] = [
  { label: '1 hour', hours: 1 },
  { label: '4 hours', hours: 4, def: true },
  { label: '24 hours', hours: 24 },
  { label: '7 days', hours: 24 * 7 },
  { label: '30 days', hours: 24 * 30 },
  { label: 'Never', hours: 0 },
];

/** The index the dialog opens on. Derived, so the flag above stays the truth. */
export const SHARE_EXPIRY_DEFAULT_INDEX: number =
  Math.max(0, SHARE_EXPIRY_CHOICES.findIndex((c) => c.def));

/**
 * The absolute instant a link stops serving, or null for never.
 *
 * ⚠ COMPUTED FROM THE BROWSER CLOCK and stored as an instant; the gate compares
 * it against the SERVER's clock at read time. A skewed machine therefore shifts
 * the window slightly — irrelevant at four hours, and far simpler than an
 * interval the database resolves. It would start to matter if anything shorter
 * than an hour were ever offered.
 */
export function shareExpiresAt(hours: number, nowMs: number): string | null {
  if (!(hours > 0)) return null;
  return new Date(nowMs + hours * 3_600_000).toISOString();
}

// ---------------------------------------------------------------------------
// Masking
// ---------------------------------------------------------------------------

/**
 * Blank out runs of three or more digits.
 *
 * Account labels are the problem: filtering to one account before sharing — the
 * natural thing to do — puts that account's full name, number and all, above the
 * fold of a public page. Same rule the Discord path uses.
 *
 * ⚠ THREE, not one. A two-digit run is "Apex 12" style shorthand and masking it
 * makes every label unreadable; a 3+ run is an account number.
 */
export function maskAccountDigits(s: string): string {
  return String(s ?? '').replace(/\d{3,}/g, (m) => '*'.repeat(m.length));
}

// ---------------------------------------------------------------------------
// The trade payload
// ---------------------------------------------------------------------------

/** One resolved discipline flag, as the visitor sees it. */
export interface ShareDisciplineFlag {
  label: string;
  tripped: boolean;
}

/** A custom tag, resolved to a name and a colour that has been validated. */
export interface ShareTag {
  name: string;
  /** Empty when the stored colour was not a `#rrggbb` literal. */
  color: string;
}

/** Exactly what a visitor receives about one trade. Nothing else may be added. */
export interface ShareTrade {
  id: string;
  date: string;
  timeIn: string;
  timeOut: string;
  contract: string;
  /** The DISPLAY label, digit-masked. Never `sourceAccount`. */
  account: string;
  side: string;
  qty: number | null;
  entry: number | null;
  exit: number | null;
  points: number | null;
  netPnl: number | null;
  /**
   * ── 🚨 THE FOUR FIELDS BELOW EXIST SO THE SHARED TABLE CAN BE THE APP'S OWN ──
   *
   * The visitor's table is rendered by `TradeTable` — the same component the
   * Day Overview uses — rather than by a second hand-rolled one. That component
   * reads these, so the payload carries them.
   *
   * ⚠ THEY PUBLISH NOTHING NEW. `netPnl` already travels; gross and
   * commissions are the same trade's money, and `value` is the figure the
   * sharer's own screen printed. The one thing that stays out is
   * `riskAtEntry` — dollars RISKED, which implies account size, and which no
   * column shows.
   */
  /** This trade in the sharer's display unit. Null where it has none. */
  value: number | null;
  grossPnl: number | null;
  fees: number | null;
  /** Drives the `syn` tag beside the symbol. */
  syntheticClose: boolean;
  /** Risk in POINTS and R only — see the header. */
  riskPoints: number | null;
  rrPlanned: number | null;
  rrRealized: number | null;
  plannedStop: number | null;
  plannedTarget: number | null;
  maePoints: number | null;
  mfePoints: number | null;
  pattern: string;
  exitModel: string;
  grade: string;
  session: string;
  regime: string;
  mistakeTags: string[];
  tags: ShareTag[];
  discipline: ShareDisciplineFlag[];
  /** `false` = never reviewed. The modal must say so rather than draw a clean sheet. */
  reviewed: boolean;
  /** Written ONLY when the sharer opted in. Absent, not empty. */
  notes?: string;
  newsEvent?: string;
  mood?: string;
  /** Data-URL screenshots, when the sharer opted in and the caps allowed. */
  shots?: string[];
}

/** The fields this module reads off a trade. A narrow structural subset. */
export interface ShareSourceTrade {
  id: string;
  timeIn: string | null;
  timeOut: string | null;
  contract: string;
  side: string;
  qty: number | null;
  entry: number | null;
  exit: number | null;
  points: number | null;
  netPnl: number | null;
  grossPnl: number | null;
  fees: number | null;
  syntheticClose: boolean;
  riskPoints: number | null;
  rrPlanned: number | null;
  rrRealized: number | null;
  plannedStop: number | null;
  plannedTarget: number | null;
  maePoints: number | null;
  mfePoints: number | null;
  patternId: string | null;
  exitModelId: string | null;
  grade: string;
  sessionLabel: string;
  marketRegime: string;
  newsEvent: string;
  mood: string;
  mistakeTags: readonly string[];
  tags: readonly string[];
  booleans: Record<string, boolean>;
  notes: string;
  annotated: boolean;
}

export interface ShareInput {
  trade: ShareSourceTrade;
  /**
   * The SESSION-RESOLVED date. Decided by the caller, because the rule lives in
   * the user's preferences and a 22:36 trade belongs to the next session — the
   * same date the report above the table was built from.
   */
  effectiveDate: string;
  /**
   * The account's DISPLAY label. ⚠ The raw broker string must never be passed
   * here; it is masked on the way through, but masking is the second guard.
   */
  accountLabel: string;
  /**
   * This trade in the sharer's DISPLAY UNIT.
   *
   * ⚠ CONVERTED BY THE CALLER, and it has to be: ticks need a tick size and R
   * needs a risk basis, both of which live in the contract catalogue — which
   * the sharer's client has and a visitor never will. Converting at share time
   * is what lets a points report stay a points report for a stranger.
   */
  value: number | null;
}

/** The registries the ids are resolved against. */
export interface ShareRegistries {
  patterns: readonly { id: string; name: string }[];
  exitModels: readonly { id: string; name: string }[];
  tags: readonly { id: string; name: string; color?: string | null }[];
  /** The discipline flags, in the order the trade form lays them out. */
  flags: readonly { key: string; label: string }[];
}

export interface ShareOptions {
  /** Include per-trade notes, news and mood. Off by default. */
  notes: boolean;
  /** Include chart screenshots. Off by default. */
  shots: boolean;
}

/** `#rrggbb` only. The value is user-authored and lands in a style attribute. */
const HEX = /^#[0-9a-fA-F]{6}$/;

const str = (v: unknown): string => (typeof v === 'string' ? v : '');
const num = (v: unknown): number | null =>
  (typeof v === 'number' && Number.isFinite(v) ? v : null);

/**
 * Serialise the trades a shared report is allowed to describe.
 *
 * ⚠ THE SET MATTERS AS MUCH AS THE FIELDS. Hand this a wider population than
 * the report lists and it publishes trades the report does not even mention —
 * readable in the payload regardless of what is drawn. Callers pass the same
 * rows the table on screen was built from.
 */
export function shareTradeData(
  rows: readonly ShareInput[],
  opts: ShareOptions,
  reg: ShareRegistries,
): ShareTrade[] {
  const patternById = new Map(reg.patterns.map((p) => [p.id, p.name]));
  const exitModelById = new Map(reg.exitModels.map((x) => [x.id, x.name]));
  const tagById = new Map(reg.tags.map((t) => [t.id, t]));

  const out: ShareTrade[] = [];
  for (const row of rows) {
    if (!row || !row.trade) continue;
    const t = row.trade;
    const b = t.booleans ?? {};
    const td: ShareTrade = {
      id: String(t.id ?? ''),
      date: row.effectiveDate,
      timeIn: str(t.timeIn),
      timeOut: str(t.timeOut),
      contract: str(t.contract),
      account: maskAccountDigits(row.accountLabel ?? ''),
      side: str(t.side),
      qty: num(t.qty),
      entry: num(t.entry),
      exit: num(t.exit),
      points: num(t.points),
      netPnl: num(t.netPnl),
      value: num(row.value),
      grossPnl: num(t.grossPnl),
      fees: num(t.fees),
      syntheticClose: t.syntheticClose === true,
      riskPoints: num(t.riskPoints),
      rrPlanned: num(t.rrPlanned),
      rrRealized: num(t.rrRealized),
      plannedStop: num(t.plannedStop),
      plannedTarget: num(t.plannedTarget),
      maePoints: num(t.maePoints),
      mfePoints: num(t.mfePoints),
      pattern: (t.patternId && patternById.get(t.patternId)) || '',
      exitModel: (t.exitModelId && exitModelById.get(t.exitModelId)) || '',
      grade: str(t.grade),
      session: str(t.sessionLabel),
      regime: str(t.marketRegime),
      mistakeTags: [...(t.mistakeTags ?? [])].map(str).filter(Boolean),
      tags: [...(t.tags ?? [])].map((id) => {
        const tag = tagById.get(id);
        if (!tag) return null;
        return {
          name: str(tag.name),
          color: HEX.test(str(tag.color)) ? str(tag.color) : '',
        };
      }).filter((x): x is ShareTag => x !== null),
      // ⚠ Resolved to [{label, tripped}] — the visitor has no flag registry to
      // map keys against, and a raw `stopNotHonored: true` would render as a
      // variable name on a public page.
      discipline: reg.flags.map((f) => ({ label: f.label, tripped: b[f.key] === true })),
      // ⚠ `annotated === false` means NEVER REVIEWED. The modal has to say so
      // rather than draw eight unticked boxes as a clean sheet.
      reviewed: t.annotated !== false,
    };
    if (opts.notes) {
      td.notes = str(t.notes);
      td.newsEvent = str(t.newsEvent);
      td.mood = str(t.mood);
    }
    out.push(td);
  }
  return out;
}

// ---------------------------------------------------------------------------
// Screenshot caps
// ---------------------------------------------------------------------------

/**
 * ⚠ THE PICTURES TRAVEL INSIDE THE PAYLOAD, NOT IN A PUBLIC BUCKET, and that is
 * what makes revoke honest. A public bucket would keep serving every copied
 * image after a link was revoked — to anyone who saved a URL, and to anyone who
 * walked `{token}/1`, `{token}/2`. Living inside the payload means the Edge
 * Function's revocation gate is the ONLY door.
 *
 * ⚠ CAPPED, and the cap is REPORTED rather than silently applied: an uncapped
 * day would put tens of megabytes into one anonymous response, and a silently
 * capped share looks complete and is not.
 */
export const SHARE_SHOT_MAX_PER_TRADE = 3;
export const SHARE_SHOT_MAX_TOTAL = 12;
export const SHARE_SHOT_MAX_BYTES = 8 * 1024 * 1024;

// ---------------------------------------------------------------------------
// 🔴 THERE IS NO `sharedDisciplinePct` ANY MORE, AND THAT IS DELIBERATE
// ---------------------------------------------------------------------------
//
// It existed for a Disc. column on a SECOND, hand-rolled trades table in the
// shared view. That table is gone: the visitor's page renders the app's own
// `TradeTable`, on the Day Overview's layout, so there is one renderer rather
// than two — and that layout has no Disc. column.
//
// ⚠ A COLUMN CANNOT SIMPLY BE ADDED BACK. `TradeTable` computes it with
// `tradeDisciplinePct`, which reads the raw `booleans` map, and this payload
// publishes the flags as resolved LABELS instead (a visitor has no flag
// registry). An empty map on a REVIEWED trade scores 100% — a clean sheet
// claimed on the trader's behalf. `SharedReport.test.tsx` fails if the layout
// ever gains the column, precisely so that trap cannot open quietly.
//
// The "an unreviewed trade is not a clean sheet" rule still travels: it is the
// `reviewed` flag above, and the trade window renders no boxes at all without it.

// ---------------------------------------------------------------------------
// Sizing the visitor's frame
// ---------------------------------------------------------------------------

/** Below this the frame is too short to be worth sizing at all. */
export const SHARE_FRAME_MIN_PX = 320;
/** A corrupt or hostile height must not create a 50,000px element. */
export const SHARE_FRAME_MAX_PX = 24_000;

/**
 * How tall to draw the report frame.
 *
 * `measured` is read from a throwaway frame over the document that will
 * actually be shown; `stored` is the number recorded at capture time.
 *
 * ── 🚨 WHY THE STORED NUMBER IS ONLY A FALLBACK ──
 *
 * The capture records the height of the stripped clone. That is right when it is
 * written and stops being right the moment the RENDERER removes anything more —
 * which it does, so a link created before that shipped is sized for a table it
 * no longer shows. A number frozen in the database cannot describe HTML the
 * renderer edits.
 *
 * ⚠ THE SLACK ON THE FALLBACK IS DELIBERATELY GENEROUS, and the asymmetry is the
 * reason. Over-estimating costs a strip of empty space before the trade list —
 * the framed body is transparent, so it reads as padding. UNDER-estimating clips
 * the report mid-table and hands the visitor a scrollbar inside a scrollbar.
 * A visitor's window can also be NARROWER than the sharer's, which wraps more
 * lines and needs MORE height than was measured; 12% did not cover that, hence a
 * proportional margin AND a flat floor.
 *
 * Returns null when neither number is usable — the caller falls back to a
 * viewport-height frame, which is degraded rather than broken.
 */
export function shareFrameHeightPx(
  measured: number | null | undefined,
  stored: number | null | undefined,
): number | null {
  const m = Number(measured) || 0;
  const s = Number(stored) || 0;
  const h = m || (s ? Math.round(s * 1.12) + 120 : 0);
  if (!(h > 0)) return null;
  return Math.min(Math.max(Math.round(h) + 8, SHARE_FRAME_MIN_PX), SHARE_FRAME_MAX_PX);
}

// ---------------------------------------------------------------------------
// The payload
// ---------------------------------------------------------------------------

/** Which report a link points at. Decides the CSS scope the frame is given. */
export type ShareKind = 'dashboard' | 'dayview' | 'calendar';

export const SHARE_KINDS: readonly ShareKind[] = ['dashboard', 'dayview', 'calendar'];

/** ⚠ A payload from the network is UNTRUSTED. Anything reading it must guard. */
export function isShareKind(v: unknown): v is ShareKind {
  return typeof v === 'string' && (SHARE_KINDS as readonly string[]).includes(v);
}

export interface SharePayload {
  /**
   * The sharer's display unit — dollars, points, ticks or R.
   *
   * ⚠ REPORT-LEVEL, because it describes the whole picture above the table as
   * well. A report captured in points whose table printed dollars is two
   * different claims on one page, which is what the first version did.
   *
   * ⚠ A payload written before this field existed has none; the viewer reads
   * that as dollars, which is what those links already showed.
   */
  unit: PnlUnit;
  title: string;
  subtitle: string;
  /** The active account label, masked. Rendered under the title. */
  accountLabel: string;
  /** The captured report markup. ⚠ ATTACKER-CONTROLLABLE — see io/share/render. */
  html: string;
  /** The height of the STRIPPED CLONE, not of the live tab. */
  contentHeight: number | null;
  trades: ShareTrade[];
  notesIncluded: boolean;
  capturedAt: string;
}

// ---------------------------------------------------------------------------
// The header line
// ---------------------------------------------------------------------------

/**
 * The account scope, as one line, for the public header.
 *
 * Ported from the old client's `getActiveAccountLabel()` (app/index.html:12758).
 *
 * ⚠ It is MASKED by the caller before it is stored — filtering to one account
 * before sharing is the natural thing to do, and it puts that account's full
 * name and number above the fold of a public page.
 */
export function activeAccountLabel(
  accounts: readonly { id: string; name?: string | null }[],
  selectedIds: readonly string[],
): string {
  const ids = selectedIds.filter(Boolean);
  if (!ids.length || ids.length === accounts.length) return 'All accounts';
  if (ids.length === 1) {
    const a = accounts.find((x) => String(x.id) === String(ids[0]));
    return a?.name || '1 account';
  }
  return `${ids.length} accounts`;
}
